"use client"

import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { Map, Search, MessageCircle, Bell, User, Plus, Home } from "lucide-react"
import { cn } from "@/lib/utils"
import { useEffect, useState } from "react"
import { createClient } from "@/lib/supabase/client"
import { useAuth } from "@/lib/auth/context"
import { usePlatform, triggerHaptic } from "@/hooks/use-platform"

export function BottomNavbar() {
  const pathname = usePathname()
  const router = useRouter()
  const { user } = useAuth()
  const { isMobile, isNative, isCapacitor, isStandalone, isIOS, isLoaded } = usePlatform()
  const [unreadMessages, setUnreadMessages] = useState(0)
  const [unreadNotifications, setUnreadNotifications] = useState(0)

  const searchParams = useSearchParams()
  // Only show on mobile, and hide on auth or admin pages
  // Also hide when a chat is open to allow input field to be visible
  // Also wait for platform info to be loaded to ensure correct links (Home -> /home vs /)
  const isChatting = pathname === "/messages" && (searchParams.has("recipientId") || searchParams.has("spotId") || searchParams.has("partyId"))
  
  const shouldShow = isLoaded && !pathname.startsWith("/auth") && !pathname.startsWith("/admin") && !isChatting


  // App mode logic consistent with root pages
  const isAppMode = isMobile && (
    isNative || 
    (isStandalone && !isIOS)
  )

  useEffect(() => {
    if (!user || !shouldShow) return

    const supabase = createClient()
    
    // Fetch initial counts
    const fetchCounts = async () => {
      // Messages
      const { count: msgCount } = await supabase
        .from("messages")
        .select("*", { count: "exact", head: true })
        .eq("recipient_id", user.id)
        .neq("is_read", true)
      
      setUnreadMessages(msgCount || 0)

      // Notifications
      const { data: userProfile } = await supabase
        .from("users")
        .select("id")
        .eq("auth_id", user.id)
        .single()
      
      if (userProfile?.id) {
        const { count: notifCount } = await supabase
          .from("notifications")
          .select("*", { count: "exact", head: true })
          .eq("user_id", userProfile.id)
          .eq("is_read", false)
        
        setUnreadNotifications(notifCount || 0)
      }
    }

    fetchCounts()

    // Real-time subscriptions
    const channel = supabase
      .channel('bottom-nav-counts')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'messages' }, fetchCounts)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'notifications' }, fetchCounts)
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [user, shouldShow])

  if (!shouldShow) return null

  const navItems = [
    { label: "Home", icon: Home, href: isMobile ? (user ? "/home" : "/") : "/" },
    { label: "Explore", icon: Search, href: "/explore" },
    { label: "Map", icon: Map, href: "/map" },
    { label: "Messages", icon: MessageCircle, href: "/messages", badge: unreadMessages },
  ]

  const handleNav = (href: string) => {
    triggerHaptic()
    if (pathname === href) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } else {
      router.push(href)
    }
  }

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white/90 dark:bg-slate-900/90 backdrop-blur-lg border-t border-slate-200 dark:border-slate-800 pb-safe md:hidden z-50">
      <div className="flex items-center justify-around h-16">
        {navItems.map((item) => {
          // Logic to prevent both Home and Explore from being active on web
          // Home is only active on the actual /home page (Native App only)
          // For web users, Home points to /explore, but we only highlight it if they are on /
          const isActive = item.label === "Home" 
            ? (isAppMode ? pathname === "/home" : pathname === "/")
            : pathname === item.href.split('?')[0]
          
          return (
            <button
              key={item.label}
              onClick={() => handleNav(item.href)}
              className={cn(
                "flex flex-col items-center justify-center w-full h-full gap-1 transition-colors relative",
                isActive ? "text-blue-500" : "text-slate-500 dark:text-slate-400"
              )}
            >
              <item.icon className={cn("w-6 h-6", isActive && "fill-current")} />
              <span className="text-[10px] font-medium">{item.label}</span>
              {item.badge > 0 && (
                <span className="absolute top-2 right-[25%] flex items-center justify-center min-w-[16px] h-[16px] px-1 bg-red-500 text-white text-[9px] font-bold rounded-full border-2 border-white dark:border-slate-900">
                  {item.badge > 99 ? "99+" : item.badge}
                </span>
              )}
            </button>
          )
        })}
      </div>
    </nav>
  )
}
